import { ROUTES, type Currency } from '@shared/utils';
import type { Product, WithContext } from 'schema-dts';
import { JsonLd } from '../../JsonLd/JsonLd';
import type { ProductVertical } from '../../PageToolbarWithNavigation/navigationConfig/types';
import type { GameInfoType } from '../GameInfo/types';

type GameProductJsonLdProps = {
  game: GameInfoType;
  gameName: string;
  productVertical: ProductVertical;
  currency: Currency;
  baseUrl: string;
};

const ADULT_AUDIENCE = {
  '@type': 'PeopleAudience',
  audienceType: 'Adults 18+',
} as const;

function getGameInfoUrl(productVertical: ProductVertical, gameName: string, baseUrl: string): string {
  if (productVertical === 'live-casino') {
    return `${baseUrl}${ROUTES.LIVE_CASINO.GAME_INFO(gameName)}`;
  }

  return `${baseUrl}${ROUTES.CASINO.GAME_INFO(gameName)}`;
}

function getProductDescription(game: GameInfoType): string | undefined {
  return game.seoDescription || game.content || game.description || undefined;
}

function GameProductJsonLd({ game, gameName, productVertical, currency, baseUrl }: GameProductJsonLdProps) {
  const productUrl = getGameInfoUrl(productVertical, gameName, baseUrl);
  const description = getProductDescription(game);
  const brandName = game.provider?.[0];
  const hasOffer = game.minimumStake != null;

  const product: WithContext<Product> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: game.name,
    alternateName: game.name,
    url: productUrl,
    sku: gameName,
    isFamilyFriendly: false,
    audience: ADULT_AUDIENCE,
    image: [game.image, game.gameImageUrlDesktop, game.gameImageUrlMobile],
    ...(description ? { description } : {}),
    ...(brandName ? { brand: { '@type': 'Brand', name: brandName } } : {}),
    ...(hasOffer
      ? {
          offers: {
            '@type': 'Offer',
            price: game.minimumStake,
            priceCurrency: currency,
            availability: 'https://schema.org/InStock',
            url: productUrl,
          },
        }
      : {}),
  };

  return <JsonLd json={product} data-testid="game-product-json-ld" />;
}

export { GameProductJsonLd, type GameProductJsonLdProps };
